import React from "react";
import { motion } from "framer-motion";
import { Smartphone, RotateCcw, Search, Timer, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";

const DeviceLockWidget: React.FC = () => {
  const { t } = useTranslation("nonHealth");
  const [isLocked, setIsLocked] = React.useState(true);
  const [lockDuration, setLockDuration] = React.useState(5);
  const [unlocking, setUnlocking] = React.useState(false);

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (isLocked) {
        setLockDuration(prev => prev + 1);
      }
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [isLocked]);

  const handleUnlock = () => {
    if (unlocking || !isLocked) return;
    setUnlocking(true);
    setTimeout(() => {
      setIsLocked(false);
      setUnlocking(false);
      // Re-lock after 5 seconds for demo purposes
      setTimeout(() => setIsLocked(true), 5000);
    }, 2000);
  };

  return (
    <motion.div
      key="deviceLock-widget"
      className="relative w-full max-w-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{
        duration: 0.5,
        type: "spring",
        stiffness: 100,
      }}
    >
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 shadow-xl border border-slate-200 relative overflow-hidden">
        {/* افکت‌های تزئینی پس‌زمینه با گرادیان‌های پویا */}
        <motion.div
          className="absolute -left-10 top-10 w-40 h-40 bg-gradient-to-br from-slate-600/15 to-gray-600/10 rounded-full blur-2xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.4, 0.7, 0.4],
            rotate: [0, 15, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute -right-10 -bottom-10 w-40 h-40 bg-gradient-to-tr from-slate-600/15 to-gray-600/10 rounded-full blur-2xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.7, 0.4],
            rotate: [0, -15, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />

        {/* خطوط امنیتی متحرک در پس‌زمینه */}
        <div className="absolute inset-0 overflow-hidden opacity-10">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={`security-line-${i}`}
              className="absolute h-px bg-gradient-to-r from-red-500/0 via-red-500 to-red-500/0"
              style={{
                top: `${20 + i * 15}%`,
                width: "150%",
                left: "-25%",
              }}
              animate={{ x: ["-25%", "25%"] }}
              transition={{
                duration: 8 - i,
                repeat: Infinity,
                ease: "linear",
                delay: i * 0.5,
              }}
            />
          ))}
        </div>

        {/* بخش اصلی ویجت قفل دستگاه با طراحی مدرن */}
        <div className="bg-gradient-to-br from-slate-50 via-gray-50 to-gray-100  p-5 rounded-xl mb-4 shadow-inner relative overflow-hidden border border-gray-100/50">
          {/* نوار وضعیت امنیت */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-gray-300/50 via-gray-500/80 to-gray-300/50 overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
              animate={{ x: ["-100%", "100%"] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          </div>

          <motion.div
            className="bg-white backdrop-blur-sm p-3 rounded-lg mb-4 shadow-sm"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{
              y: -2,
              boxShadow: "0 4px 12px rgba(0,0,0, 0.15)",
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <motion.div
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center ml-2 text-red-500"
                  animate={{ rotate: [0, 5, 0, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <Smartphone className="w-4 h-4" />
                </motion.div>
                <div>
                  <p className="text-slate-700 text-xs font-medium">
                    {t("widgets.deviceLock.childDevice")}
                  </p>
                  <motion.p
                    className="text-slate-800 font-bold text-sm"
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    {isLocked ? t("widgets.deviceLock.lockedState") : t("widgets.deviceLock.unlocked")}
                  </motion.p>
                </div>
              </div>
              <motion.div
                className={`text-xs font-bold px-2 py-1 rounded-full flex items-center ${isLocked ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <motion.span
                  className={`inline-block w-1.5 h-1.5 rounded-full ml-1 ${isLocked ? "bg-red-500" : "bg-green-500"}`}
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                {isLocked ? t("widgets.deviceLock.lockActive") : t("widgets.deviceLock.active")}
              </motion.div>
            </div>
          </motion.div>

          <motion.button
            onClick={handleUnlock}
            className={`w-full text-white py-3 rounded-lg text-sm font-medium relative overflow-hidden group shadow-lg ${isLocked ? "bg-gradient-to-r from-slate-400 to-gray-500 shadow-gray-500/20" : "bg-gradient-to-r from-green-400 to-emerald-500 shadow-green-500/20"}`}
            whileHover={{
              scale: 1.02,
              boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.2)",
              y: -2,
            }}
            whileTap={{ scale: 0.98, y: 0 }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 15,
            }}
          >
            {/* افکت موج پیشرفته روی دکمه */}
            <motion.span
              className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0"
              initial={{ x: "-100%" }}
              animate={unlocking ? { x: "100%" } : { x: "100%" }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: "easeInOut",
              }}
            />

            {/* افکت درخشش لبه‌ها */}
            <motion.span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 border border-white/30 rounded-lg"
              animate={{ scale: [0.95, 1.05, 0.95] }}
              transition={{ duration: 2, repeat: Infinity }}
            />

            <span className="relative z-10 flex items-center justify-center font-semibold">
              <motion.div
                animate={unlocking ? { rotate: 360 } : { rotate: [0, 10, 0, -10, 0] }}
                transition={unlocking ? { duration: 1, repeat: Infinity } : { duration: 3, repeat: Infinity }}
              >
                <RotateCcw className="w-5 h-5 ml-2" />
              </motion.div>
              {unlocking ? t("widgets.deviceLock.unlocking") : isLocked ? t("widgets.deviceLock.unlock") : t("widgets.deviceLock.unlocked")}
            </span>
          </motion.button>
        </div>

        {/* کارت‌های اطلاعاتی با طراحی جدید */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
          <motion.div
            className="bg-gradient-to-br from-red-50 to-red-100/70 p-3.5 rounded-xl shadow-sm border border-red-200 relative overflow-hidden"
            whileHover={{
              scale: 1.03,
              boxShadow: "0 8px 20px rgba(239, 68, 68, 0.15)",
              y: -3,
            }}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, type: "spring" }}
          >
            {/* افکت نور پس‌زمینه */}
            <motion.div
              className="absolute -right-6 -bottom-6 w-16 h-16 bg-gradient-to-br from-red-400/20 to-red-500/10 rounded-full blur-xl"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3],
                rotate: [0, 45, 0],
              }}
              transition={{ duration: 5, repeat: Infinity }}
            />

            <div className="flex items-center mb-2">
              <motion.div
                className="w-5 h-5 bg-red-200 rounded-full flex items-center justify-center ml-1.5 text-red-600"
                animate={{ rotate: [0, 360] }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <Search className="w-3 h-3" />
              </motion.div>
              <div className="text-red-700 font-bold">{t("widgets.deviceLock.securityStatus")}</div>
            </div>

            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center">
                <motion.span
                  className={`inline-block w-2 h-2 rounded-full ml-1.5 ${isLocked ? "bg-red-500" : "bg-green-500"}`}
                  animate={{
                    opacity: [1, 0.3, 1],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <p className="text-gray-700 font-medium">{isLocked ? t("widgets.deviceLock.lockActive") : t("widgets.deviceLock.unlocked")}</p>
              </div>
              <motion.span
                className="text-red-500 text-xs font-bold bg-red-100 px-1.5 py-0.5 rounded-md"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {t("widgets.deviceLock.level", { level: 3 })}
              </motion.span>
            </div>

            <div className="relative w-full h-1.5 bg-red-200 rounded-full overflow-hidden mt-1">
              <motion.div
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-red-500 to-rose-600"
                initial={{ width: 0 }}
                animate={{ width: isLocked ? "85%" : "0%" }}
                transition={{
                  duration: 1,
                  delay: 0.5,
                  ease: "easeOut",
                }}
              >
                <motion.div
                  className="absolute top-0 right-0 h-full w-5 bg-white/30"
                  animate={{ x: ["-100%", "300%"] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatType: "loop",
                  }}
                />
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className="bg-gradient-to-br from-rose-50 to-pink-100/70 p-3.5 rounded-xl shadow-sm border border-pink-200 relative overflow-hidden"
            whileHover={{
              scale: 1.03,
              boxShadow: "0 8px 20px rgba(244, 114, 182, 0.15)",
              y: -3,
            }}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, type: "spring" }}
          >
            {/* افکت نور پس‌زمینه */}
            <motion.div
              className="absolute -left-6 -bottom-6 w-16 h-16 bg-gradient-to-br from-pink-400/20 to-pink-500/10 rounded-full blur-xl"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3],
                rotate: [0, -45, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                delay: 0.5,
              }}
            />

            <div className="flex items-center mb-2">
              <motion.div
                className="w-5 h-5 bg-pink-200 rounded-full flex items-center justify-center ml-1.5 text-pink-600"
                animate={{
                  y: [0, -2, 0],
                  rotate: [0, 5, 0, -5, 0],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Timer className="w-3 h-3" />
              </motion.div>
              <div className="text-pink-700 font-bold">{t("widgets.deviceLock.timeDetails")}</div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <p className="text-gray-600 text-xs">{t("widgets.deviceLock.lockTime")}</p>
                <motion.p
                  className="text-pink-700 font-semibold"
                  animate={{ scale: [1, 1.03, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
                </motion.p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-gray-600 text-xs">{t("widgets.deviceLock.duration")}</p>
                <div className="flex items-center">
                  <motion.span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-pink-500 ml-1"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <p className="text-pink-700 font-semibold">{t("widgets.deviceLock.minutes", { count: lockDuration })}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* پیام اعلان/توصیه با طراحی تعاملی */}
        <motion.div
          className="bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-200 p-3 rounded-xl relative overflow-hidden"
          whileHover={{
            scale: 1.02,
            boxShadow: "0 5px 15px rgba(251, 146, 60, 0.15)",
          }}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, type: "spring" }}
        >
          {/* افکت‌های نور پس‌زمینه */}
          <motion.div
            className="absolute -left-10 -top-10 w-20 h-20 bg-orange-400/10 rounded-full blur-xl"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.5, 0.3],
              rotate: [0, 45, 0],
            }}
            transition={{ duration: 8, repeat: Infinity }}
          />

          <div className="flex items-start">
            <div className="relative ml-3">
              <motion.div
                className="absolute -inset-1.5 bg-orange-400/20 rounded-full blur-md"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.div
                className="relative bg-gradient-to-br from-orange-200 to-amber-300 w-8 h-8 rounded-full flex items-center justify-center shadow-sm"
                animate={{
                  rotate: [0, 10, 0, -10, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 5, repeat: Infinity }}
              >
                <Lightbulb className="w-4 h-4 text-amber-700" />
              </motion.div>
            </div>

            <div className="flex-1">
              <h6 className="text-amber-700 font-bold text-xs mb-1">
                {t("widgets.deviceLock.securityTip")}
              </h6>
              <p className="text-amber-800 text-xs leading-relaxed">
                {t("widgets.deviceLock.securityTipText")}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DeviceLockWidget;